import React, {useEffect, useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';

import {FormattedMessage, useIntl} from 'react-intl';

import {IStore} from 'redux/interface';
import {
  cmServiceAsyncActions,
  getCmFilters,
  getCmServices,
  getTranslations
} from 'redux/pageAsyncActions';
import {useGetTranslations} from 'utils/hooks/useGetTranslations';
import {Pages, TranslationKeys} from 'utils/constants/routeConstants';

import NotFoundImg from 'assets/image/common/notFound.png';
import BookIcon from 'assets/image/library/book.png';

import Loader from 'components/loader';
import NotFoundInfo from 'components/not-found-info';
import {ICmFilter} from '../../redux/cm-service/interfaces';
import {CmResourceFilters} from '../../components/cm-filters/CmResourceFilters';
import {maxServiceCards} from '../../utils/constants/constants';
import CmCardSection from '../../components/cm-card-section';
import {useNavigate} from 'react-router-dom';

import './styles.scss';

const isCmFilter = (x: ICmFilter | null | undefined): x is ICmFilter => x !== null && x !== undefined;

export const CmResource: React.FunctionComponent = () => {
  const {
    services,
    filters,
    page
  } = useSelector((state: IStore) => state.cm_service);
  const {
    loading
  } = useSelector((state: IStore) => state.status);
  const {
    settings
  } = useSelector((state: IStore) => state.auth);
  const dispatch = useDispatch();
  const intl = useIntl();
  const [selectedFilters, setSelectedFilters] = useState<ICmFilter[]>([]);
  const history = useNavigate();

  const translationKey = useMemo(() => {
    const resourcePage = settings?.pages.find((p) => p.name.toLowerCase() === Pages.CmResource.toLowerCase());

    return resourcePage?.translationKey || TranslationKeys.CmResource;
  }, [settings]);

  useGetTranslations({translationKey});

  const title = useMemo(() => {
    const resourcePage = settings?.pages.find((p) => p.name.toLowerCase() === Pages.CmResource.toLowerCase());

    return resourcePage?.title || Pages.CmResource;
  }, [settings]);

  useEffect(() => {
    dispatch(cmServiceAsyncActions[getCmFilters]());
  }, []);

  function loadService({
    filter,
    offset
  }: { filter: ICmFilter[]; offset: number }) {
    const filtersWithValue = filter.filter(isCmFilter).filter(f => f.inputValue);

    dispatch(cmServiceAsyncActions[getCmServices](
      {
        filter: filtersWithValue,
        offset,
        limit: maxServiceCards
      }
    ));
  }

  useEffect(() => {
    loadService({filter: [], offset: 0});
  }, []);

  function handleLoadMore(offset: number) {
    loadService({
      filter: selectedFilters,
      offset
    });
  }

  function handleChangeFilters(changedFilters: ICmFilter[]) {
    setSelectedFilters(changedFilters);
    loadService({
      filter: changedFilters,
      offset: 0
    });
  }

  function handleOpenCard(srvId: number) {
    history(`/cmresource/${srvId}`);
  }

  function renderFilters() {
    if (filters) {
      return <CmResourceFilters filters={filters} onChange={handleChangeFilters}/>;
    }

    return null;
  }

  return <section className={'cm-resource container'}>
    {
      !loading.includes(getTranslations) && <>
        <h1 className="cm-resource__title"><img src={BookIcon} alt="" className="cm-resource__icon"/>
          <FormattedMessage
            id={`gritx.common.navigation.${title}`}
            defaultMessage={title}
          />
        </h1>
        <div className="cm-resource__filters">
          {
            loading.includes(getCmFilters) ? <Loader/> : renderFilters()
          }
        </div>
        <div className="cm-resource__cards">
          {services.length === 0 && filters && !loading.includes(getCmServices) ? <NotFoundInfo
            title={intl.formatMessage({
              id: 'gritx.cmresource.sectionCards.notFound',
              defaultMessage: 'Sorry, but nothing can be found. Try the search or apply fewer filters.'
            })}
            image={NotFoundImg}
          /> : page && <CmCardSection
            showLoader={loading.includes(getCmServices)}
            cards={services}
            onLoadMore={handleLoadMore}
            onClickCard={handleOpenCard}
            page={page}
          />}
        </div>
      </>
    }
  </section>;
};
